import React, { useState, useRef, useEffect, useContext, createContext } from 'react';
import { Search, Lock } from 'lucide-react';
import { UNITS } from './constants';

// ══════════════════════════════════════════════════════════════
// Context — надається з MainApp (App.js), містить власні одиниці юзера
// і функції керування + прапор PRO для paywall
// ══════════════════════════════════════════════════════════════
export const CustomUnitsContext = createContext({
  units: [],
  isPro: true,
  showPaywall: null,
});

// ══════════════════════════════════════════════════════════════
// Компонент — чистий пікер з пошуком.
// Стандартні UNITS завжди доступні. Власні — dimmed+lock для FREE.
// НЕ приймає free-form ввід (додавання нової — лише через модалку "Мої одиниці")
// ══════════════════════════════════════════════════════════════
export default function UnitPicker({ value, onChange, className = '', style = {}, disabled = false }) {
  const { units: customUnits, isPro, showPaywall } = useContext(CustomUnitsContext);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const searchRef = useRef(null);

  const openMenu = (e) => {
    if (disabled) return;
    e?.stopPropagation();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 180),
      });
    }
    setIsOpen(true);
    setQuery('');
  };

  const closeMenu = () => {
    setIsOpen(false);
    setQuery('');
  };

  useEffect(() => {
    if (isOpen && searchRef.current) {
      const t = setTimeout(() => searchRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const select = (u) => {
    onChange(u);
    closeMenu();
  };

  const handleCustomClick = (u) => {
    if (isPro) {
      select(u);
    } else {
      if (typeof showPaywall === 'function') showPaywall('custom_units');
      closeMenu();
    }
  };

  const q = query.trim().toLowerCase();
  const filterFn = (u) => !q || u.toLowerCase().includes(q);
  const filteredStd = UNITS.filter(filterFn);
  const filteredCustom = (customUnits || []).filter(filterFn);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openMenu}
        disabled={disabled}
        className={className}
        style={style}
        title={value || ''}
      >
        {value || '—'}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0"
            style={{ zIndex: 9998 }}
            onClick={closeMenu}
          />
          <div
            className="fixed bg-white border border-gray-200 rounded-lg shadow-xl flex flex-col overflow-hidden"
            style={{
              top: menuPos.top,
              left: menuPos.left,
              minWidth: menuPos.width,
              maxWidth: 260,
              maxHeight: 320,
              zIndex: 9999
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Пошук */}
            <div className="p-2 border-b bg-gray-50 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') closeMenu(); }}
                  placeholder="Пошук..."
                  className="w-full pl-7 pr-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                  style={{ fontSize: 12 }}
                />
              </div>
            </div>

            {/* Список */}
            <div className="overflow-y-auto py-1">
              {filteredStd.map(u => (
                <button
                  key={u}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); select(u); }}
                  className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${value === u ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  {u}
                </button>
              ))}

              {filteredCustom.length > 0 && (
                <>
                  <div className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase border-t border-gray-100 mt-1 pt-2 flex items-center justify-between">
                    <span>Мої одиниці</span>
                    {!isPro && <span className="normal-case font-bold" style={{ color: '#f97316' }}>PRO</span>}
                  </div>
                  {filteredCustom.map(u => (
                    <button
                      key={u}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); handleCustomClick(u); }}
                      className={`w-full text-left px-3 py-1.5 text-sm transition-colors flex items-center justify-between ${
                        !isPro ? 'opacity-50 hover:bg-orange-50' :
                        value === u ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      title={!isPro ? 'Доступно на PRO' : ''}
                    >
                      <span>{u}</span>
                      {!isPro && <Lock className="w-3 h-3" style={{ color: '#f97316' }} />}
                    </button>
                  ))}
                </>
              )}

              {filteredStd.length === 0 && filteredCustom.length === 0 && (
                <div className="px-3 py-4 text-center text-xs text-gray-400">
                  Не знайдено
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
